import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { dashboardLayoutApi } from '../services/widgetDataService';
import type { DashboardLayout, DashboardData, WidgetLayout } from '../types/dashboard';
import { DEFAULT_LAYOUT } from '../types/dashboard';

interface DashboardContextValue {
  dashboard: DashboardData | null;
  layout: DashboardLayout;
  editMode: boolean;
  loading: boolean;
  setEditMode: (v: boolean) => void;
  updateLayout: (widgets: WidgetLayout[]) => void;
  addWidget: (widget: WidgetLayout) => void;
  removeWidget: (widgetId: string) => void;
  saveLayout: () => Promise<void>;
  resetLayout: () => void;
  generateWidgetId: () => string;
}

const DashboardContext = createContext<DashboardContextValue | null>(null);

export function useDashboard() {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useDashboard must be used within DashboardProvider');
  return ctx;
}

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [layout, setLayout] = useState<DashboardLayout>(DEFAULT_LAYOUT);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const widgetCounter = useRef(100);

  // Load dashboard on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await dashboardLayoutApi.getMyDefault();
        setDashboard(data);
        if (data.layout?.widgets && data.layout.widgets.length > 0) {
          setLayout(data.layout);
        }
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const generateWidgetId = useCallback(() => {
    widgetCounter.current += 1;
    return `w${widgetCounter.current}_${Date.now()}`;
  }, []);

  const updateLayout = useCallback((widgets: WidgetLayout[]) => {
    setLayout((prev) => ({ ...prev, widgets }));
  }, []);

  const addWidget = useCallback((widget: WidgetLayout) => {
    setLayout((prev) => ({
      ...prev,
      widgets: [...prev.widgets, widget],
    }));
  }, []);

  const removeWidget = useCallback((widgetId: string) => {
    setLayout((prev) => ({
      ...prev,
      widgets: prev.widgets.filter((w) => w.i !== widgetId),
    }));
  }, []);

  const saveLayout = useCallback(async () => {
    if (!dashboard) return;
    try {
      const updated = await dashboardLayoutApi.saveLayout(dashboard.id, layout);
      setDashboard(updated);
    } catch (err) {
      console.error('Failed to save layout:', err);
      throw err;
    }
  }, [dashboard, layout]);

  const resetLayout = useCallback(() => {
    setLayout(DEFAULT_LAYOUT);
  }, []);

  // Auto-save with debounce when layout changes in edit mode
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  useEffect(() => {
    if (!editMode || !dashboard) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await dashboardLayoutApi.saveLayout(dashboard.id, layoutRef.current);
      } catch {
        // Silent fail on auto-save
      }
    }, 2000);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [layout, editMode, dashboard]);

  return (
    <DashboardContext.Provider value={{
      dashboard, layout, editMode, loading,
      setEditMode, updateLayout, addWidget, removeWidget,
      saveLayout, resetLayout, generateWidgetId,
    }}>
      {children}
    </DashboardContext.Provider>
  );
};
